import React, { useEffect, useState } from 'react';
import { formatearFechaCompleta} from '../../../utils/utils'; // Importamos desde el archivo utils.js

/**
 * Componente para renderizar una tarjeta de solicitud
 * @param {Object} props - Propiedades del componente
 * @param {Solicitud} props.solicitud - La solicitud a renderizar
 * @param {Function} props.onCotizar - Función para cotizar la solicitud
 * @param {Function} props.onRechazar - Función para rechazar la solicitud
 * @returns {JSX.Element} - El elemento JSX de la tarjeta
 */
const RendersolicitudCard = ({ solicitud, onCotizar, onRechazar }) => {

  const calcularDiasRestantes = (fechaEntrega) => {
        if (!fechaEntrega) return 0;
        const hoy = new Date();
        const entrega = new Date(fechaEntrega);
        const diferencia = entrega - hoy;
        return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
      };

  const diasRestantes = calcularDiasRestantes(solicitud.fechaentrega);

  // Función para calcular y formatear el tiempo restante
  const calcularTiempoRestante = (fechaEntrega) => {
      try {
        const ahora = new Date();
        const entrega = new Date(fechaEntrega);
        
        // Diferencia en milisegundos
        const diferencia = entrega - ahora;
        
        // Si la fecha ya pasó
        if (diferencia < 0) {
          return { valor: 'Vencido', clase: 'bg-red-500 text-white' };
        }
        
        // Convertir a horas
        const horasRestantes = Math.floor(diferencia / (1000 * 60 * 60));
        
        // Si es menos de 24 horas, mostrar en horas
        if (horasRestantes < 24) {
          return { 
            valor: `${horasRestantes} horas`, 
            clase: 'bg-red-100 text-red-800'
          };
        }
        
        // Convertir a días
        const diasRestantes = Math.ceil(diferencia / (1000 * 60 * 60 * 24));
        
        // Determinar la clase según urgencia
        let clase = 'bg-green-100 text-green-800'; // Por defecto (más de 7 días)
        
        if (diasRestantes <= 2) {
          clase = 'bg-red-100 text-red-800'; // Urgente (2 días o menos)
        } else if (diasRestantes <= 7) {
          clase = 'bg-blue-100 text-blue-800'; // Próximo (entre 3 y 7 días)
        }
        
        return { valor: `${diasRestantes} días`, clase };
      } catch (error) {
        console.error('Error al calcular tiempo restante:', error);
        return { valor: 'Fecha inválida', clase: 'bg-gray-100 text-gray-800' };
      }
    };

  const tiempoRestante = calcularTiempoRestante(solicitud.fechaentrega || solicitud.fechaEntrega);

  //Estilo dependiendo del tipo de servicio
  const getServicioStyles = () => {
    const servicioLower = (solicitud.Servicio || '').toLowerCase();

    switch (servicioLower){
      case 'taller':
        return{
          headerBg: 'bg-gradient-to-r from-blue-600 to-blue-800',
          icon: (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
          ),
          additionalInfo: (
            <>
              <div className="mt-2 p-2 bg-purple-50 rounded-md">
                <h4 className="font-semibold text-purple-700">Detalles del Taller</h4>
                <p className="text-sm text-gray-600">
                  { `${solicitud.resumen}`}
                </p>
                <p className="text-sm text-gray-600">
                {solicitud.objetoFaltante && solicitud.objetoFaltante.formatoEntrega 
                  ? `Formato de entrega: ${solicitud.objetoFaltante.formatoEntrega}` 
                  : 'Formato de entrega no definido'}
              </p>
                
              </div>
            </>
          )
        }

      case 'tesis':
        return{
          headerBg: 'bg-gradient-to-r from-green-600 to-green-800',
          icon: (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          ),
          additionalInfo: (
            <>
              <div className="mt-2 p-2 bg-green-50 rounded-md">
                <h4 className="font-semibold text-green-700">Información de Tesis</h4>
                <p className="text-sm text-gray-600">
                  {`Tema tesis: ${solicitud.objetoFaltante.temaTesis}`}
                </p>
                <p className="text-sm text-gray-600">
                  {`Carrera: ${solicitud.objetoFaltante.carrera}`}
                </p>
                <p className="text-sm text-gray-600">
                  {`Universidad: ${solicitud.objetoFaltante.universidad}`}
                </p>
                <p className="text-sm text-gray-600">
                  {`Cantidad paginas: ${solicitud.objetoFaltante.cantidadPaginas}`}
                </p>
                <p className="text-sm text-gray-600">
                  {`Normas de citacion: ${solicitud.objetoFaltante.normasCitacion}`}
                </p>
                
              </div>
            </>
          )
        }

      case 'parcial':
      case 'quiz':
        const isQuiz = servicioLower === 'quiz';

        return {
          headerBg: isQuiz 
            ? 'bg-gradient-to-r from-pink-500 to-pink-700' 
            : 'bg-gradient-to-r from-indigo-600 to-indigo-800',
          icon: (
            <svg 
              className="w-5 h-5 mr-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              {isQuiz 
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              }
            </svg>
          ),
          additionalInfo: (
            <>
              <div className={`mt-2 p-2 ${isQuiz ? 'bg-pink-50' : 'bg-indigo-50'} rounded-md`}>
                <h4 className={`font-semibold ${isQuiz ? 'text-pink-700' : 'text-indigo-700'}`}>
                  {isQuiz ? 'Detalles del Quiz' : 'Detalles del Parcial'}
                </h4>
                
                <p className="text-sm text-gray-600">
                  {isQuiz 
                    ? (`Temas de quiz: ${solicitud.objetoFaltante.temasExamen}`)
                    : (`Temas de examen: ${solicitud.objetoFaltante.temasExamen}`)
                  }
                </p>
                
                <p className="text-sm text-gray-600">
                  {isQuiz 
                    ? (`Duración examen: ${solicitud.objetoFaltante.duracionExamen}`)
                    : (`Duración de quiz: ${solicitud.objetoFaltante.duracionExamen}`)
                  }
                </p>
              </div>
            </>
          )
        };

      case 'clases personalizadas':
        return {
          headerBg: 'bg-gradient-to-r from-yellow-500 to-yellow-700',
          icon: (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
            </svg>
          ),
          additionalInfo: (
            <>
              <div className="mt-2 p-2 bg-yellow-50 rounded-md">
                <h4 className="font-semibold text-yellow-700">Detalles de Clases</h4>
                <p className="text-sm text-gray-600">
                  {`Temas de Asesoria: ${solicitud.resumen}`}
                </p>
                <p className="text-sm text-gray-600">
                  {`Horas de Asesoria: ${solicitud.objetoFaltante.numeroHoras}`}
                </p>
              </div>
            </>
          )
        };

      default:
        return{
          headerBg: 'bg-gradient-to-r from-blue-600 to-blue-800',
          icon: null,
          additionalInfo: null
        }
    }
  }

  const servicioInfo = getServicioStyles();


  //carta de solicitud
  return (
        <div key={solicitud.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 mb-4">
          
          {/* Encabezado de la tarjeta con color dinámico según servicio */}
          <div className={`${servicioInfo.headerBg} text-white p-4 rounded-t-lg`}> {/* Añadido rounded-t-lg para bordes suaves */}
            <div className="flex justify-between items-start">
              {/* --- Contenido de la Izquierda --- */}
              <div className="flex items-start">
                {/* Ícono principal del servicio */}
                {servicioInfo.icon && (
                  <span className="mr-3 mt-1 flex-shrink-0">{servicioInfo.icon}</span>
                )}

                {/* Contenedor para el texto */}
                <div>
                  {/* Título Principal (Materia original) */}
                  <h3 className="font-bold text-lg leading-tight">
                    {solicitud.materia}-{solicitud.id}
                  </h3>

                  {/* --- Materia sugerida por IA (Aparece condicionalmente) --- */}
                  {/* Solo se muestra si es diferente a la original y no está vacía */}
                  {solicitud.materia !== solicitud.predicionesIA.materiaIA && solicitud.predicionesIA.materiaIA && (
                    <div className="flex items-center text-sm mt-1 opacity-90">
                      {/* Ícono de estrella/magia que representa la IA */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <p className="font-medium">{solicitud.predicionesIA.materiaIA}</p>
                    </div>
                  )}
                </div>
              </div>

              {/*
              # {solicitud.cotizaciones.length} cotizaciones
              */}

              {/* --- Contenido de la Derecha (Tiempo) --- */}
              <div className={`ml-4 flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium ${tiempoRestante.clase}`}>
                {tiempoRestante.valor}
              </div>
            </div>
          </div>

          {/* Cuerpo de la tarjeta */}
          <div className="p-4">

            {/* Contenido adicional específico por tipo de servicio */}
            {servicioInfo.additionalInfo} 
            
            {/* Información adicional (fecha, cliente) */}
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div>
                <span className="text-gray-500">Fecha entrega:</span>
                <p className="font-medium">{formatearFechaCompleta(solicitud.fechaentrega)}</p>
              </div>
              <div>
                <span className="text-gray-500">Empresa:</span>
                <p className="font-medium">{solicitud.nombreEmpresa}</p>
              </div>
            </div>

            {/* Estado */}
            <div className="mb-4">
              <div className="flex items-center">
                <div className="h-2.5 w-2.5 rounded-full bg-blue-500 mr-2"></div>
                <span className="text-blue-600 font-medium">
                  {solicitud.estado || 'Cotización pendiente'}
                </span>
              </div>
            </div>
            
            {/* Botones de acción */}
            <div className="flex space-x-2">
              <button
                onClick={() => onCotizar(solicitud)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition flex items-center justify-center"
              >
                <svg 
                  className="w-4 h-4 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Cotizar
              </button>
              <button
                onClick={() => onRechazar(solicitud)}
                className="px-4 py-2 border border-red-500 text-red-500 hover:bg-red-50 rounded transition"
              >
                Rechazar
              </button>
            </div>
            
          </div>
        </div>
      )


}

export default RendersolicitudCard
